<script setup>
import { computed, h, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from 'dashboard/components-next/filter/operators';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import WorkflowTriggerConditions from './WorkflowTriggerConditions.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import {
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import {
  getWorkflowActionNodeTitle,
  getWorkflowActionStepDescriptionI18nKeys,
  getWorkflowActionStepVisual,
  getWorkflowTriggerDescriptionI18nKeys,
} from './workflowCanvasStepTheme';
import { getWorkflowEventVisual } from './workflowEventTheme';
import WorkflowTriggerEventSelect from './WorkflowTriggerEventSelect.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  activePanel: {
    type: String,
    required: true,
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  automationRuleEvents: {
    type: Array,
    required: true,
  },
  automationActionTypes: {
    type: Array,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
});

defineEmits(['cancel']);

const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { operators } = useOperators();

const isEditMode = computed(() => props.mode === 'edit');

const cancelKey = computed(() =>
  isEditMode.value
    ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT'
    : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const hasAutomationMutated = computed(() => {
  return Boolean(
    automation.value?.conditions[0]?.values ||
      automation.value?.actions[0]?.action_params?.length
  );
});

const hasConditionErrors = computed(() =>
  Object.keys(props.errors).some(key => key.startsWith('condition_'))
);

const hasActionErrors = computed(() =>
  Object.keys(props.errors).some(key => key.startsWith('action_'))
);

const panelHeader = computed(() => {
  const p = props.activePanel;

  if (p === 'trigger-conditions') {
    const visual = getWorkflowEventVisual(automation.value?.event_name);
    const keys = getWorkflowTriggerDescriptionI18nKeys();
    return {
      title: t('AUTOMATION.FLOW.NODE_TRIGGER'),
      longDescription: t(keys.long),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
    };
  }

  const m = /^action-(\d+)$/.exec(p);
  if (m) {
    const index = Number(m[1]);
    const action = automation.value?.actions?.[index];
    if (!action) return null;

    const visual = getWorkflowActionStepVisual(action.action_name);
    const keys = getWorkflowActionStepDescriptionI18nKeys(action.action_name);
    return {
      title: getWorkflowActionNodeTitle(
        action.action_name,
        automation.value.actions,
        index,
        t
      ),
      longDescription: t(keys.long),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
    };
  }

  return null;
});

const triggerConditionsRef = useTemplateRef('triggerConditionsRef');

const validateConditions = () =>
  triggerConditionsRef.value?.validate?.() ?? true;

const resetConditionRowValidation = () => {
  triggerConditionsRef.value?.resetValidation?.();
};

const syncCustomAttributeTypesForAutomation = () => {
  if (!automation.value?.conditions?.length) return;
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

defineExpose({
  validateConditions,
  resetConditionRowValidation,
  syncCustomAttributeTypesForAutomation,
});
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <div
      v-if="automation"
      class="flex h-full min-h-0 w-full flex-col gap-4 bg-n-solid-2 p-4 dark:bg-n-solid-2"
    >
      <div
        v-if="panelHeader"
        class="min-h-0 shrink-0 border-b border-n-weak pb-4 dark:border-n-strong"
      >
        <div class="flex flex-col gap-1.5">
          <div class="flex items-center gap-3">
            <HugeIcon
              :icon="panelHeader.icon"
              :size="24"
              class="shrink-0"
              :class="panelHeader.iconClass"
            />
            <h2 class="m-0 text-lg font-semibold leading-snug text-n-slate-12">
              {{ panelHeader.title }}
            </h2>
          </div>
          <p class="m-0 text-xs leading-relaxed text-n-slate-12">
            {{ panelHeader.longDescription }}
          </p>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div
          v-show="activePanel === 'trigger-conditions'"
          class="flex flex-col gap-4"
        >
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1.5">
              <WorkflowTriggerEventSelect
                v-model="automation.event_name"
                :events="automationRuleEvents"
                :has-error="Boolean(errors.event_name)"
                @change="onEventChange()"
              />
              <span v-if="errors.event_name" class="message">
                {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
              </span>
            </div>
            <p
              v-if="!isEditMode && hasAutomationMutated"
              class="m-0 text-[11px] leading-snug text-n-slate-12"
            >
              {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
            </p>
          </div>

          <WorkflowTriggerConditions
            ref="triggerConditionsRef"
            :conditions="automation.conditions"
            :filter-types="filterTypes"
            :has-errors="hasConditionErrors"
            @append="appendNewCondition"
            @remove="removeFilter"
          />
        </div>

        <div
          v-for="(action, i) in automation.actions"
          v-show="activePanel === `action-${i}`"
          :key="`inspector-action-${i}`"
          class="flex flex-col gap-2"
        >
          <label>
            {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </label>
          <ul
            class="grid list-none rounded-xl border-solid p-3 outline outline-1 -outline-offset-1"
            :class="
              hasActionErrors && errors[`action_${i}`]
                ? 'bg-n-ruby-2/50 outline-n-ruby-5'
                : 'outline-n-weak dark:outline-n-strong'
            "
          >
            <AutomationActionInput
              v-model="automation.actions[i]"
              :action-types="automationActionTypes"
              dropdown-max-height="max-h-[7.5rem]"
              :dropdown-values="getActionDropdownValues(action.action_name)"
              :show-action-input="
                showActionInput(automationActionTypes, action.action_name)
              "
              :error-message="
                errors[`action_${i}`]
                  ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                  : ''
              "
              :initial-file-name="
                isEditMode ? getFileName(action, automation.files) : ''
              "
              @reset-action="resetAction(i)"
              @remove-action="removeAction(i)"
            />
            <div class="pt-2">
              <NextButton
                icon="i-lucide-plus"
                blue
                faded
                sm
                :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
                @click="appendNewAction"
              />
            </div>
          </ul>
        </div>
      </div>

      <div
        class="mt-auto flex w-full shrink-0 flex-row justify-end gap-2 border-t border-n-weak pt-4 dark:border-n-strong"
      >
        <NextButton
          faded
          slate
          type="reset"
          :label="$t(cancelKey)"
          @click.prevent="$emit('cancel')"
        />
      </div>
    </div>
  </div>
</template>
